<script setup>
import * as Sentry from "@sentry/vue";
import { computed, ref, toRefs } from "vue";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import {
  GENERAL_GROUP_VISA_SERVICE,
  SPAIN_GROUP_VISA_SERVICE,
} from "@/components/program-manager/sessions/constants.js";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import { useTenantTheming } from "@/composables/useTenantTheming";

const props = defineProps({
  application: {
    type: Object,
    default: () => ({}),
    required: true,
  },
  goToNextStep: {
    type: Function,
    default: () => {},
    required: true,
  },
});

const { application } = toRefs(props);

const { secondaryColorClassLinks } = useTenantTheming();

const groupVisaSubmit = ref(null);
const groupVisaSubmit2 = ref(null);

const submissionError = ref("");

const currentOrder = computed(() => application.value?.primaryOrder?.value);
const groupVisaProducts = [SPAIN_GROUP_VISA_SERVICE.id, GENERAL_GROUP_VISA_SERVICE.id];

const acceptedVisaOrderPayload = computed(() => {
  const order = currentOrder.value;
  const existingMiscProductIds = order?.misc_product_ids ?? [];
  const miscProductIds = new Set([...existingMiscProductIds, availableGroupVisaProduct.value]);
  return {
    ...currentOrder.value,
    misc_product_ids: [...Array.from(miscProductIds)],
  };
});

const rejectedVisaOrderPayload = computed(() => {
  const order = currentOrder.value;
  const existingMiscProductIds = order?.misc_product_ids ?? [];
  const miscProductIdsMinusVisas = existingMiscProductIds.filter((id) => !groupVisaProducts.includes(id));
  return { ...order, misc_product_ids: miscProductIdsMinusVisas };
});

const programSession = computed(() => {
  return application?.value?.session?.value;
});

const availableProducts = computed(
  () => programSession.value?.available_products?.filter((product) => groupVisaProducts.includes(product?.id)) ?? [],
);

const isSelectedByDefault = computed(() => {
  return availableProducts.value?.some((product) => Boolean(product.selected_by_default));
});

const availableGroupVisaProduct = computed(() => {
  const visaProductsInSession = availableProducts.value.map((product) => product?.id);
  return visaProductsInSession?.[0] ?? undefined;
});

const groupVisaPrice = computed(() => {
  if (!availableGroupVisaProduct.value) return undefined;

  if (SPAIN_GROUP_VISA_SERVICE.id === availableGroupVisaProduct.value) {
    return SPAIN_GROUP_VISA_SERVICE.price_in_cents / 100;
  } else if (GENERAL_GROUP_VISA_SERVICE.id === availableGroupVisaProduct.value) {
    return GENERAL_GROUP_VISA_SERVICE.price_in_cents / 100;
  }

  return undefined;
});

const yesGroupVisaButtonText = computed(() => {
  return isSelectedByDefault.value ? "Continue with Group Visa Process" : "Yes, add group visa";
});

const noGroupVisaButtonText = computed(() => {
  return isSelectedByDefault.value ? "Opt Out of Group Visa Process" : "No thanks";
});

// Methods
const submitGroupVisa = async (accepted = false) => {
  try {
    groupVisaSubmit.value.startLoading();
    groupVisaSubmit2.value.startLoading();

    const orderId = currentOrder.value?.id;
    if (!orderId) {
      throw new Error("Cannot submit group visa decision: no primary order found");
    }
    if (accepted) {
      await application.value.updateOrder(orderId, acceptedVisaOrderPayload.value, "GroupVisa.accepted");
    } else {
      await application.value.updateOrder(orderId, rejectedVisaOrderPayload.value, "GroupVisa.rejected");
    }

    props.goToNextStep();
  } catch (error) {
    submissionError.value = error;
    Sentry.captureException(error, {
      tags: {
        troubleshooting_note: "Group Visa step in submission error (Connect Application).",
        order_id: currentOrder.value?.id,
      },
    });
  } finally {
    groupVisaSubmit.value.stopLoading();
    groupVisaSubmit2.value.stopLoading();
  }
};
</script>
<template>
  <div class="text-indigo-base text-left bg-white w-full border-collapse flex flex-col gap-7 px-4 md:p-10 max-w-4xl">
    <div>
      <div v-if="isSelectedByDefault" class="grid grid-cols-1 gap-6 md:gap-7 md:pt-4 text-xl text-center">
        <p>
          Based on your program and home address, your university highly recommends students to use API's Group Visa
          Process, which ensures your documents are submitted correctly and on time. Visa delays are one of the most
          common reasons students are unable to participate in their study abroad or internship program.
        </p>
        <p>
          The Group Visa Service fee is ${{ groupVisaPrice }}, in addition to the applicable consulate fee (paid
          separately to the consulate). You can review current consulate fees <a
            id="studentVisasLink"
            href="https://apiabroad.com/student-visas/"
            target="_blank"
            :class="`${secondaryColorClassLinks} font-semibold underline`"
            >here</a
          >.
        </p>
        <p>
          If you live near the appropriate consulate and prefer to apply independently, you may opt out of this
          service. Please note that you will be fully responsible for managing your visa appointment and requirements.
        </p>
      </div>
      <div v-else class="grid grid-cols-1 gap-6 md:gap-7 md:pt-4 text-xl text-center">
        <p>
          Based on your address and program it looks like you are eligible for API&rsquo;s Group Visa Process. This
          makes getting your Visa much easier and less stressful for you since we appear at the consulate on your
          behalf. In addition to the API Group Visa fee, you will be responsible for paying the consulate fee, you can
          find those fees <a
            id="studentVisasLink"
            href="https://apiabroad.com/student-visas/"
            target="_blank"
            :class="`${secondaryColorClassLinks} font-semibold underline`"
            >here</a
          >.
        </p>
        <p>Would you like to add a Group Visa to your experience for ${{ groupVisaPrice }}?</p>
      </div>
    </div>
    <div>
      <ButtonWithSpinner
        id="groupVisaButton"
        ref="groupVisaSubmit"
        data-cy="groupVisaButton"
        type="submit"
        variant="secondary"
        variant-type="block"
        button-height="min-h-[58px] md:min-h-[60px] w-full"
        :grey-disabled-class="true"
        @click="submitGroupVisa(true)"
      >
        <div class="flex items-center justify-center">
          <span class="pr-3 uppercase">{{ yesGroupVisaButtonText }}</span>
          <ArrowRight custom-class="w-4 h-4" />
        </div>
      </ButtonWithSpinner>
    </div>
    <div>
      <ButtonWithSpinner
        id="groupVisaButton2"
        ref="groupVisaSubmit2"
        data-cy="groupVisaButton2"
        type="submit"
        variant="secondary-outlined"
        variant-type="normal"
        button-height="min-h-[58px] md:min-h-[60px] w-full"
        :grey-disabled-class="true"
        @click="submitGroupVisa(false)"
      >
        <div class="flex items-center justify-center">
          <span class="pr-3 uppercase">{{ noGroupVisaButtonText }}</span>
          <ArrowRight custom-class="w-4 h-4" />
        </div>
      </ButtonWithSpinner>
    </div>
    <div v-if="isSelectedByDefault">
      <p class="text-sm text-center">
        By opting out of API's Group Visa Service, you acknowledge that you are fully responsible for submitting your
        visa application correctly and on time. Visa delays or denials may impact your ability to participate in your
        program.
      </p>
    </div>
  </div>
</template>
